import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
  adminIamApi,
  type AdminConsoleUser,
  type AdminIamPermission,
  type AdminIamRole,
  type AdminRolePreview,
} from '../../../api/adminConsole';
import {
  permissionLockedForActor,
  permissionPickerSections,
  rolePermissionChange,
} from '../../../lib/adminRolesView';
import { IamErrorAlert } from './IamErrorAlert';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const RISK_COLOR = { standard: 'default', sensitive: 'warning', critical: 'error' } as const;

function labelFor(catalog: readonly AdminIamPermission[], key: string): string {
  return catalog.find((c) => c.key === key)?.label ?? key;
}

/**
 * Create or edit a role. The picker is grouped by catalog group with risk + MFA chips; keys the
 * actor does not hold are locked (adding or removing them would be an escalation). Before saving,
 * the change is previewed — for an existing role via `POST /admin/iam/roles/:id/preview`, which
 * also reports the active holders affected and any rule violation.
 */
export function RoleEditorDialog({
  open,
  role,
  catalog,
  actor,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** null = create. */
  role: AdminIamRole | null;
  catalog: readonly AdminIamPermission[];
  actor: AdminConsoleUser | undefined;
  onClose: () => void;
  onSaved: (saved: AdminIamRole) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [step, setStep] = useState<'edit' | 'review'>('edit');
  const [preview, setPreview] = useState<AdminRolePreview | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setKey('');
    setDescription(role?.description ?? '');
    setSelected([...(role?.permissions ?? [])]);
    setStep('edit');
    setPreview(null);
  }, [open, role]);

  const sections = useMemo(() => permissionPickerSections(catalog, role?.permissions ?? []), [catalog, role]);
  const localChange = rolePermissionChange(role?.permissions ?? [], selected);
  const nothingChanged =
    !!role &&
    localChange.added.length === 0 &&
    localChange.removed.length === 0 &&
    name.trim() === role.name &&
    description.trim() === (role.description ?? '');

  const previewM = useMutation({
    mutationFn: () => (role ? adminIamApi.previewRole(role.id, selected) : Promise.resolve(null)),
    onSuccess: (p) => {
      setPreview(p);
      setStep('review');
    },
  });

  const save = useMutation({
    mutationFn: () =>
      role
        ? adminIamApi.updateRole(role.id, { name: name.trim(), description: description.trim(), permissions: selected })
        : adminIamApi.createRole({
            name: name.trim(),
            description: description.trim(),
            permissions: selected,
            ...(key.trim() ? { key: key.trim() } : {}),
          }),
    onSuccess: async (saved) => {
      await onSaved(saved);
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      previewM.reset();
      save.reset();
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (k: string, on: boolean) =>
    setSelected((prev) => (on ? [...new Set([...prev, k])] : prev.filter((x) => x !== k)));

  const added = preview?.added ?? localChange.added;
  const removed = preview?.removed ?? localChange.removed;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {role ? `Edit role: ${role.name}` : 'New role'}
        </Typography>
        {step === 'review' && (
          <Typography variant="body2" color="text.secondary">
            Review the change before saving.
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <IamErrorAlert error={previewM.error ?? save.error} />
        {step === 'edit' ? (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField label="Name" size="small" required value={name} onChange={(e) => setName(e.target.value)} fullWidth />
              {!role && (
                <TextField
                  label="Key (optional)"
                  size="small"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  helperText="Derived from the name when blank."
                  fullWidth
                />
              )}
            </Stack>
            <TextField
              label="Description"
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Typography variant="caption" color="text.secondary">
              {selected.length} permission{selected.length === 1 ? '' : 's'} selected. Locked keys are ones you do
              not hold; you can neither add nor remove them.
            </Typography>
            <Stack spacing={2}>
              {sections.map((section) => (
                <Box key={section.group}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    {section.group}
                  </Typography>
                  <Stack sx={{ mt: 0.5 }}>
                    {section.items.map((item) => {
                      const locked = permissionLockedForActor(actor, item.key);
                      const checked = selected.includes(item.key);
                      const control = (
                        <FormControlLabel
                          sx={{ m: 0, alignItems: 'center' }}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={locked}
                              onChange={(e) => toggle(item.key, e.target.checked)}
                            />
                          }
                          label={
                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
                                {item.key}
                              </Typography>
                              <Chip size="small" variant="outlined" color={RISK_COLOR[item.risk]} label={item.risk} />
                              {item.requiresMfa && <Chip size="small" variant="outlined" label="MFA" />}
                              {item.deprecatedAt && <Chip size="small" variant="outlined" label="Deprecated" />}
                            </Stack>
                          }
                        />
                      );
                      return locked ? (
                        <Tooltip key={item.key} title="You do not hold this permission" placement="top-start">
                          <span>{control}</span>
                        </Tooltip>
                      ) : (
                        <Box key={item.key}>{control}</Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        ) : (
          <Stack spacing={2}>
            {preview?.violation && (
              <Alert severity="error">
                {preview.violation.error}{' '}
                <Chip size="small" variant="outlined" label={preview.violation.code} sx={{ fontFamily: MONO, height: 20 }} />
              </Alert>
            )}
            {role && preview && (
              <Alert severity={preview.affectedHolders > 0 ? 'warning' : 'info'}>
                {preview.affectedHolders} active holder{preview.affectedHolders === 1 ? '' : 's'} will be affected.
              </Alert>
            )}
            {!role && (
              <Alert severity="info">
                New role <strong>{name.trim() || '—'}</strong> with {selected.length} permission
                {selected.length === 1 ? '' : 's'}. Nobody holds it until it is granted.
              </Alert>
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Added ({added.length})
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {added.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None</Typography>
                ) : (
                  added.map((k) => <Chip key={k} size="small" color="success" variant="outlined" label={`+ ${labelFor(catalog, k)}`} title={k} />)
                )}
              </Stack>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Removed ({removed.length})
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {removed.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None</Typography>
                ) : (
                  removed.map((k) => <Chip key={k} size="small" color="error" variant="outlined" label={`− ${labelFor(catalog, k)}`} title={k} />)
                )}
              </Stack>
            </Box>
            {role && (name.trim() !== role.name || description.trim() !== (role.description ?? '')) && (
              <Typography variant="body2" color="text.secondary">
                Name or description also changes.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        {step === 'review' && <Button onClick={() => setStep('edit')}>Back</Button>}
        {step === 'edit' ? (
          <Button
            variant="contained"
            disabled={!name.trim() || nothingChanged || previewM.isPending}
            startIcon={previewM.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={() => {
              if (role) previewM.mutate();
              else setStep('review');
            }}
          >
            Review changes
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={save.isPending || !!preview?.violation}
            onClick={() => save.mutate()}
          >
            {role ? 'Save role' : 'Create role'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
